import {
  Component,
  OnInit,
  OnDestroy,
  input,
  output,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  catchError,
  interval,
  Observable,
  Subscription,
  takeUntil,
  Subject,
  skip,
  debounceTime
} from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

import { Tag } from 'src/app/entities/tag/tag';
import { Project } from 'src/app/entities/project/project';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { Uuid } from 'src/app/entities/value-objects/uuid/uuid';
import { User } from 'src/app/entities/user/user';
import { DateService } from 'src/app/services/date-service/date.service';
import { TimeTrackRepository } from 'src/app/services/remote-data-service/repositories/time-track-repository/time-track.repository';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { StoreService } from 'src/app/services/store-service/store.service';
import log from 'loglevel';
import { SelectListComponent } from '../select-list/select-list.component';
import { BaseComponent } from '../base-component/base.component';


/**
 * Type alias to define the possible states for resuming a timer.
 *
 * It can be 'false' (not resuming), 'started' (resuming a started track),
 * or 'duplicated' (duplicating an existing track).
 */
type ResumingTrack = false | 'started' | 'duplicated';


/**
 * A component that allows users to track their time entries.
 *
 * It includes features to start, stop, and resume time tracking,
 * associate time entries with projects and tags, and manage billable status.
 */
@Component({
  selector: 'app-time-entry-player',
  templateUrl: './time-entry-player.component.html',
  styleUrls: ['./time-entry-player.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatAutocompleteModule,
    SelectListComponent
  ]
})
export class TimeEntryPlayerComponent extends BaseComponent implements OnInit, OnDestroy {


  /**
   * A logger instance for logging component activity.
   */
  protected override logger = log.getLogger('TimeEntryPlayer');


  /**
   * The time track entry to be displayed or edited.
   */
  trackEntry = input<TimeTrack | null | undefined>(null);


  /**
   * An array of projects to be displayed in the project selection dropdown.
   */
  projects = input.required<Project[]>();


  /**
   * An array of tags to be displayed for tagging time entries.
   */
  tags = input.required<Tag[]>();


  /**
   * A boolean indicating whether the timer is currently running.
   */
  isRunning = input(false);


  /**
   * A boolean indicating whether the timer is on or off.
   */
  timerOn = false;


  /**
   * An event emitter that emits the time track when the timer is started or stopped.
   */
  enterPressed = output<TimeTrack>();


  /**
   * The form group for managing time entry input, including description, tags, project, and billable status.
   */
  public form = new FormGroup({
    inputText: new FormControl(''),
    selectedTags: new FormControl<string[]>([]),
    selectedProject: new FormControl<string | null>(null),
    isBillable: new FormControl(false),
  });


  /**
   * An observable for filtering track suggestions based on user input.
   */
  filteredOptions: Observable<string[]> = new Observable<string[]>();


  /**
   * The currently selected project value.
   */
  selectedProjectValue: Project | undefined;


  /**
   * An array of track suggestions based on the selected project.
   */
  trackSuggestions: string[] = [];


  /**
   * The start time of the timer.
   */
  startTime: Date | null = null;


  /**
   * The elapsed time in milliseconds.
   */
  private elapsedTime = 0;

  /**
   * The timer subscription.
   */
  private timerSubscription?: Subscription;

  /**
   * A Subject that emits when the component is destroyed.
   */
  private activeTimeTrack: TimeTrack | null = null;

  /**
   * A Subject that emits when the component is destroyed.
   */
  private destroy$ = new Subject<void>();

  /**
   * Local variable of current user
   */
  protected currentUser?: User;

  /**
   * Observable of current user
   */
  protected currentUser$: Observable<User | null>;


  /**
   * Observable of timer player resumer
   */
  protected timerPlayerResumer$: Observable<TimeTrack | null>;


  // Inject dependencies
  constructor(
    protected timeTrackRep: TimeTrackRepository,
    protected dateService: DateService,
    protected snackBar: SnackbarService,
    protected store: StoreService,
    protected cdr: ChangeDetectorRef
  ) {
    super();

    this.currentUser$ = this.store.currentUser;

    this.timerPlayerResumer$ = store.timerPlayerResume;

    // Handle timer state based on isRunning input
    effect(() => {
      if (this.isRunning() && !this.timerSubscription) {
        this.startTimer('started');
      } else if (!this.isRunning() && this.timerSubscription) {
        this.stopTimer();
      }
    });

    effect(() => {
      this.setOptionsFromTags(this.tags());
      this.setOptionsFromProjects(this.projects());
    })

    // Get current user
    this.currentUser$.pipe(
      catchError(() => []),
      takeUntil(this.destroy$)
    ).subscribe((user) => {
      this.currentUser = user || undefined;
    });

    this.timerPlayerResumer$.pipe(
      catchError(() => []),
      takeUntil(this.destroy$)
    ).subscribe((timeTrack) => {
      if (timeTrack) {
        this.checkOnTrackResume(timeTrack);
        this.store.startTracking(null);
      }
    })
  }


  checkOnTrackResume(track: TimeTrack) {
    const resumer = track;

    if (resumer.id === this.trackEntry()?.id) {
      return;
    }

    if (this.trackEntry()?.isOngoing()) {
      this.stopTimer();
      this.startTimer('duplicated', resumer);
    } else {
      this.startTimer('duplicated', resumer);
    }
  };


  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * @param changes: SimpleChanges - An object containing the current and previous property values.
   */
  // ngOnChanges(): void {
  //   this.reset()
  // }

  /**
   * Lifecycle hook called after the component has been initialized.
   * It initializes the form, retrieves track suggestions, and sets up autocomplete.
   */
  ngOnInit(): void {
    this.reset();
    this.timerOn = this.isRunning();

  }


  /**
   * Lifecycle hook called when the component is destroyed.
   * It unsubscribes from any active subscriptions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.timerSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }


  /**
   * Retrieves Tag objects from an array of tag IDs.
   *
   * @param string: string[] - An array of tag IDs.
   */
  getTagsFromStrings(string: string[] | null | undefined): Tag[] {
    if (!string) {
      return [];
    }

    return this.tags().filter(tag => string.includes(tag.id))
  }

  /**
   * Retrieves Project objects from a string.
   *
   * @param string: string - project id.
   */
  getProjectFromString(string: string | null | undefined): Project | null | undefined {
    return this.projects().find(project => project.id === string)
  }


  /**
   * Toggles the timer between start and stop states.
   */
  toggleTimer(): void {
    if (this.timerOn) {
      this.stopTimer();
    } else {
      this.startTimer();
    }
  }


  /**
   * Starts the timer, updating the elapsed time every 100 milliseconds.
   *
   * @param isResuming - A boolean indicating whether the timer is being resumed from a previous state.
   * @param timeTrack - The time track to resume, if applicable. Defaults to the current track entry.
   */
  private startTimer(
    isResuming: ResumingTrack = false,
    timeTrack = this.trackEntry()
  ): void {
    const track = timeTrack;
    this.timerOn = true;

    // Clear any existing timer subscription first to avoid duplicates
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;

    // Don't start if form is invalid (only for new tracks)
    if (!isResuming && !this.form.valid) {
      this.logger.warn('Cannot start timer with invalid form');
      return;
    }

    switch (isResuming) {
      case false: {
        const project = this.getProjectFromString(this.form.value.selectedProject) || undefined;
        const tags = this.getTagsFromStrings(this.form.value.selectedTags) || [];

        // Create new track for fresh starts
        this.startTime = new Date();
        this.activeTimeTrack = new TimeTrack(
          Uuid.generateId(),
          this.form.value.inputText || '',
          this.startTime,
          null,
          { delete: false, update: false },
          project,
          this.currentUser,
          tags,
        );

        this.timeTrackRep.addNewTrack(this.activeTimeTrack).pipe(
          catchError(() => {
            this.reset();
            return [];
          }),
          takeUntil(this.destroy$)
        ).subscribe(() => {
          this.enterPressed.emit(this.activeTimeTrack!);
        });

        break;
      }
      case 'started': {
        // If resuming, ensure we have the track and startTime

        if (track && track.isOngoing()) {
          this.startTime = track.from;
          this.activeTimeTrack = track;

          // Initialize elapsedTime correctly based on the original start time
          this.elapsedTime = Date.now() - track.from.getTime();
        } else {
          this.logger.warn('Cannot resume without a valid ongoing track');
          return; // Can't resume without a valid ongoing track
        }

        break;
      }
      case 'duplicated': {
        if (track) {

          this.timeTrackRep.addNewTrack(track).pipe(
            catchError(() => {
              this.reset();
              return [];
            }),
            takeUntil(this.destroy$)
          ).subscribe();
        }

        break;
      }
    }


    this.form.valueChanges.pipe(
      skip(1), // Skip initial form setup and reset
      debounceTime(300),
      takeUntil(this.destroy$) // Unsubscribe when the component is destroyed
    ).subscribe((values) => {
      if (!track) return;

      const timeTrack = new TimeTrack(
        track.id,
        values.inputText || '',
        track.from,
        track.to,
        track.permissions,
        this.getProjectFromString(values.selectedProject) || undefined,
        track.author,
        this.getTagsFromStrings(values.selectedTags),
        track.time_spent
      );

      this.timeTrackRep.updateTrack(timeTrack).pipe(
        catchError(() => {
          return [];
        }),
      ).subscribe();
    })


    // Use standard interval for timer display
    this.timerSubscription = interval(100).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.startTime) {
        // Always calculate elapsed time from the original start time
        // This ensures accurate timing even after page navigation
        this.elapsedTime = Date.now() - this.startTime.getTime();
        this.cdr.detectChanges();
      }
    });
  }


  /**
   * Restarts the timer if there is an ongoing track.
   */
  public restartTimerIfNeeded(): void {
    const track = this.trackEntry();
    if (track?.isOngoing()) {
      // Calculate elapsed time since track started
      this.startTime = track.from;
      this.elapsedTime = Date.now() - track.from.getTime();
      this.activeTimeTrack = track;

      // Always restart the timer to ensure it's running with the correct elapsed time
      this.startTimer('started');
    }
  }


  /**
   * Stops the timer and finalizes the time tracking process.
   */
  private stopTimer(): void {
    this.timerOn = false;

    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;

    const trackInput = this.trackEntry();

    if (!trackInput && !this.activeTimeTrack) {
      this.reset();
      return;
    }

    const endTime = new Date();
    const formValues = this.form.value;

    const updatedTrack = new TimeTrack(
      this.activeTimeTrack?.id || trackInput!.id,
      formValues.inputText || '',
      this.startTime || trackInput!.from,
      endTime,
      this.activeTimeTrack?.permissions || trackInput!.permissions,
      this.getProjectFromString(formValues.selectedProject) || undefined,
      this.currentUser,
      this.getTagsFromStrings(formValues.selectedTags),
    );

    this.timeTrackRep.updateTrack(updatedTrack).pipe(
      catchError(() => {
        return [];
      })
    ).subscribe(() => {
      this.enterPressed.emit(updatedTrack);
      this.reset();
    });
  }


  /**
   * Formats the elapsed time into a human-readable string (HH:MM:SS).
   */
  formatElapsedTime(): string {
    const totalSeconds = Math.floor(this.elapsedTime / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }


  /**
   * Resets the form and timer to their initial states.
   */
  public reset(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = undefined;

    const track = this.trackEntry();

    if (track?.isOngoing()) {
      const selectedTags: string[] = track.tags.map(tag => tag.id) || [];
      const selectedProject: string | null = track.project?.id || null;

      this.form.patchValue({
        inputText: track.title || '',
        selectedTags: selectedTags,
        selectedProject: selectedProject,
        isBillable: false
      });

      this.setSelectedOption(this.selectListTags(), null, selectedTags);
      this.setSelectedOption(this.selectListProjects(), selectedProject);

      this.startTime = track.from;
      this.elapsedTime = Date.now() - track.from.getTime();
      this.activeTimeTrack = track;

      if (this.isRunning()) {
        this.startTimer('started');
      }

    } else {
      this.elapsedTime = 0;
      this.startTime = null;
      this.activeTimeTrack = null;
    }
  }
}
